import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Form, Image } from 'react-bootstrap';
import phoneCountryList from '../../data/phoneCountryList';
import { detectGeoPhoneCountry } from '../../services/geoService';
import { FALLBACK_PHONE_COUNTRY, findPhoneCountryFromGeo } from '../../utils/phoneCountryLookup';

function getFlagSrc(flagImg) {
  if (!flagImg) return '';
  if (typeof flagImg === 'string') return flagImg;
  return flagImg.default || flagImg;
}

function buildPhone(country, national) {
  const digits = String(national || '').replace(/\D/g, '');
  if (!digits || !country?.countryCode) return '';
  return `${country.countryCode}${digits}`;
}

function parsePhone(full, defaultCountry = FALLBACK_PHONE_COUNTRY) {
  if (!full || !String(full).trim()) {
    return { country: defaultCountry, national: '' };
  }
  const normalized = String(full).trim();
  const sorted = [...phoneCountryList].sort(
    (a, b) => b.countryCode.length - a.countryCode.length
  );
  for (const country of sorted) {
    if (normalized.startsWith(country.countryCode)) {
      return {
        country,
        national: normalized.slice(country.countryCode.length),
      };
    }
  }
  return { country: defaultCountry, national: normalized.replace(/\D/g, '') };
}

const PhoneCountryInput = ({
  id,
  value = '',
  onChange,
  placeholder = 'Enter phone number',
  className = '',
  isInvalid = false,
  disabled = false,
  required = false,
  autoDetectCountry = true,
}) => {
  const userPickedCountryRef = useRef(false);
  const geoAppliedRef = useRef(false);

  const initialPhoneRef = useRef(null);
  if (initialPhoneRef.current === null) {
    initialPhoneRef.current = parsePhone(value);
  }

  const [selectedCountry, setSelectedCountry] = useState(initialPhoneRef.current.country);
  const [nationalNumber, setNationalNumber] = useState(initialPhoneRef.current.national);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!autoDetectCountry || userPickedCountryRef.current || geoAppliedRef.current) {
      return undefined;
    }

    const parsed = parsePhone(value);
    if (parsed.national) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const geo = await detectGeoPhoneCountry();
      if (cancelled || userPickedCountryRef.current) return;

      const country = findPhoneCountryFromGeo(geo);
      if (!country) return;

      geoAppliedRef.current = true;
      setSelectedCountry(country);
    })();

    return () => {
      cancelled = true;
    };
  }, [autoDetectCountry, value]);

  const filteredCountries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return phoneCountryList;
    return phoneCountryList.filter(
      (item) =>
        item.countryName.toLowerCase().includes(q) ||
        item.countryCode.toLowerCase().includes(q) ||
        item.countryCode.replace('+', '').includes(q.replace(/^\+/, ''))
    );
  }, [search]);

  const emitChange = (country, national) => {
    onChange?.(buildPhone(country, national));
  };

  const handleCountrySelect = (item) => {
    userPickedCountryRef.current = true;
    setSelectedCountry(item);
    setSearch('');
    emitChange(item, nationalNumber);
  };

  const handleNationalChange = (e) => {
    const next = e.target.value;
    setNationalNumber(next);
    emitChange(selectedCountry, next);
  };

  return (
    <div data-input-flag>
      <Dropdown className="input-group">
        <Dropdown.Toggle
          as="button"
          type="button"
          className="btn btn-light border arrow-none bg-light"
          disabled={disabled}
          id={id ? `${id}-country` : undefined}
        >
          <Image
            src={getFlagSrc(selectedCountry.flagImg)}
            alt=""
            className="options-flagimg"
            height="20"
            style={{ borderRadius: '5px' }}
          />
          <span className="countrylist-codeno text-muted ms-1">
            {selectedCountry.countryCode}
          </span>
        </Dropdown.Toggle>

        <Form.Control
          type="tel"
          id={id}
          className={`form-control rounded-end flag-input ${className} ${isInvalid ? 'is-invalid' : ''}`}
          placeholder={placeholder}
          value={nationalNumber}
          onChange={handleNationalChange}
          disabled={disabled}
          required={required}
          autoComplete="tel-national"
        />

        <Dropdown.Menu
          as="ul"
          className="list-unstyled w-100 dropdown-menu-list mb-0"
          style={{ minWidth: '280px' }}
        >
          <li className="p-2 px-3 pt-1 searchlist-input list-unstyled">
            <Form.Control
              type="text"
              size="sm"
              className="form-control-sm border search-countryList"
              placeholder="Search country name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            />
          </li>
          <li className="list-unstyled">
            <div
              className="dropdown-menu-list px-3 pb-2"
              style={{ maxHeight: '220px', overflowY: 'auto' }}
            >
              {filteredCountries.map((item) => (
                <Dropdown.Item
                  as="button"
                  type="button"
                  key={item.id}
                  className="dropdown-item d-flex border-0 bg-transparent w-100 text-start"
                  onClick={() => handleCountrySelect(item)}
                >
                  <div className="flex-shrink-0 me-2">
                    <Image
                      src={getFlagSrc(item.flagImg)}
                      alt=""
                      className="options-flagimg"
                      height="20"
                    />
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex">
                      <div className="country-name me-1">{item.countryName}</div>
                      <span className="countrylist-codeno text-muted">{item.countryCode}</span>
                    </div>
                  </div>
                </Dropdown.Item>
              ))}
              {filteredCountries.length === 0 && (
                <div className="text-muted small px-2 py-1">No countries found</div>
              )}
            </div>
          </li>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default PhoneCountryInput;
